<template>
  <aside
    class="sidebar z-[100]"
    :class="{ 'sidebar--collapsed': sidebarCollapsed }"
  >
    <button
      class="sidebar__collapse-trigger"
      @click="expandButtonClick"
    >
      <i class="fas fa-chevron-left" />
    </button>
    <div
      class="flex flex-col overflow-auto justify-between flex-1"
    >
      <nav class="flex flex-col gap-2">
        <nuxt-link
          v-for="(item, index) in navItems"
          :key="index"
          :to="item.to"
          class="sidebar__nav-item"
        >
          <div @click="trackSidebarClick(item.title)">
            <i :class="`far fa-${item.icon} sidebar__nav-icon`" />
            <span class="sidebar__nav-text">{{ item.title }}</span>
          </div>
        </nuxt-link>
        <button
          type="button"
          data-test="product-updates-button"
          class="sidebar__nav-item sidebar__nav-button"
          @click="openProductUpdates"
        >
          <i class="far fa-bell sidebar__nav-icon" />
          <span class="sidebar__nav-text">Product Updates</span>
        </button>
        <div v-if="canAccessAMT || canAccessAdminClaims || canAccessAdminPolicySearch">
          <kudo-divider class="my-2" />
          <div class="flex flex-col gap-y-2">
            <nuxt-link
              v-if="canAccessAMT"
              class="sidebar__nav-item"
              to="/admin/agency-management"
            >
              <i class="far fa-folder-gear sidebar__nav-icon" />
              <span class="sidebar__nav-text">Agency Management</span>
            </nuxt-link>
            <nuxt-link
              v-if="canAccessAdminClaims"
              class="sidebar__nav-item"
              to="/admin/claims/cat-tags"
            >
              <i class="far fa-house-crack sidebar__nav-icon" />
              <span class="sidebar__nav-text">Claims</span>
            </nuxt-link>
            <nuxt-link
              v-if="canAccessAdminPolicySearch"
              class="sidebar__nav-item"
              to="/admin/policy-search"
            >
              <i class="far fa-magnifying-glass sidebar__nav-icon" />
              <span class="sidebar__nav-text">Policy Search</span>
            </nuxt-link>
          </div>
        </div>
      </nav>
    </div>
  </aside>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { KudoDivider } from '@openly-engineering/kudo'

const SIDEBAR_COLLAPSED_LOCAL_STORAGE_KEY = 'sidebar-collapsed'

export default {
  components: {
    KudoDivider,
  },
  setup() {
    const { $featureFlags } = useNuxtApp()

    return {
      FEATURE_FLAG_IS_AGENCY_MANAGEMENT_TOOL_ENABLED: $featureFlags.useFlag('ops-scale-is-agency-management-tool-enabled'),
      FEATURE_FLAG_CLAIMS_IS_CAT_TAG_ADMIN_AVAILABLE: $featureFlags.useFlag('claims-is-cat-tag-admin-available'),
      FEATURE_FLAG_UI_ACCOUNTS_ENABLED: $featureFlags.useFlag('ui-accounts-enabled'),
    }
  },
  data() {
    return {
      sidebarCollapsed: true,
    }
  },
  computed: {
    ...mapState('self', ['isOpenly']),
    navItems() {
      return [
        {
          to: this.FEATURE_FLAG_UI_ACCOUNTS_ENABLED ? '/account/search' : '/dashboards/search',
          title: this.FEATURE_FLAG_UI_ACCOUNTS_ENABLED ? 'Account Search' : 'Search',
          icon: 'magnifying-glass',
          disabled: false,
        },
        {
          to: '/dashboards/eligible-quotes',
          title: 'Quotes',
          icon: 'file-contract',
          disabled: false,
        },
        {
          to: '/dashboards/upcoming-renewals',
          title: 'Renewals',
          icon: 'arrows-retweet',
          disabled: false,
        },
        {
          to: '/dashboards/non-renewals',
          title: 'Non-Renewals',
          icon: 'ban',
          disabled: false,
        },
      ]
    },
    canAccessAMT() {
      const permissions = this.$store.state.self.permissions
      const canAccessAMT = permissions.AgencyCreateUpdate || permissions.UserCreateUpdate || permissions.AgencyRead || permissions.UserRead
      return Boolean(canAccessAMT && this.FEATURE_FLAG_IS_AGENCY_MANAGEMENT_TOOL_ENABLED)
    },
    canAccessAdminClaims() {
      return (Boolean(this.$store.state.self.permissions?.CatastropheTrackingCreateUpdateDelete) || Boolean(this.$store.state.self.permissions?.CatastropheTrackingRead)) && this.FEATURE_FLAG_CLAIMS_IS_CAT_TAG_ADMIN_AVAILABLE
    },
    canAccessAdminPolicySearch() {
      return this.isOpenly && this.FEATURE_FLAG_UI_ACCOUNTS_ENABLED
    },
  },
  mounted() {
    if (window.innerWidth < 768) {
      this.sidebarCollapsed = true
      return
    }

    try {
      const storedSidebarCollapsed = window.localStorage.getItem(SIDEBAR_COLLAPSED_LOCAL_STORAGE_KEY)

      if (storedSidebarCollapsed !== null) {
        this.sidebarCollapsed = storedSidebarCollapsed === 'true'
      }
    } catch {
      // Ignore localStorage access errors.
    }
  },
  methods: {
    ...mapActions('self', ['logout']),
    async openAgencyResources() {
      await window.analytics.track('Agency Resources Click')
      window.open('https://help.openly.com/hc/en-us/articles/15532386227479-Agent-Resources', '_blank', 'noopener,noreferrer')
    },
    openHelpCenter() {
      window.analytics?.track('Help Center Side Navigation')
      window.open('https://help.openly.com/hc/en-us', '_blank')
    },
    openProductUpdates() {
      this.trackSidebarClick('Product Updates')
      window.userpilot?.trigger('resource_center:l--vwzCKdb')
    },
    trackSidebarClick(linkTitle) {
      window.analytics?.track('Sidebar Menu Click', {
        LinkClicked: linkTitle,
      })
    },
    expandButtonClick() {
      this.sidebarCollapsed = !this.sidebarCollapsed
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_LOCAL_STORAGE_KEY, String(this.sidebarCollapsed))
      } catch {
        // Ignore localStorage access errors.
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.router-link-active {
  @apply bg-plum-lighter text-plum-dark border-l-4;

  i {
    @apply text-plum-dark;
  }

  &.sidebar__nav-item:not(.dropdown-title) {
    @apply hover:text-plum-dark border-plum-dark;
  }
}

.sidebar {
  @apply absolute md:relative bg-white border-r border-gray-light flex flex-col py-4 shadow transition-all duration-300 ease-in-out w-full md:w-[258px] shrink-0 h-full;

  &__collapse-trigger {
    @apply rounded-full flex items-center justify-center w-6 h-6 mt-[6px] border border-gray text-gray-darker hover:bg-gray-lightest bg-white transition-all ease-in-out delay-150 absolute right-0 translate-x-1/2 shadow;
  }

  &__nav-item {
    @apply text-sm py-2 px-3 flex items-center border-l-4 border-white cursor-pointer text-left text-nowrap;

    &:hover:not(.dropdown-title) {
      @apply text-plum-dark border-plum-dark
    }
  }

  &__nav-button {
    @apply w-full bg-transparent;
  }

  &__nav-icon {
    @apply mr-3 w-6 text-center;
  }

  &--collapsed {
    @apply w-[72px] relative;

    .sidebar__nav-text {
      @apply hidden;
    }

    .sidebar__collapse-trigger {
      @apply rotate-180;
    }

    .sidebar__nav-item {
      @apply justify-center;
    }

    .sidebar__nav-icon {
      @apply mr-0;
    }
  }
}
</style>
